import { SCORING } from '../config/gameConfig';

export interface ScoreSnapshot {
  score: number;
  fares: number;
  bonuses: number;
  nearMisses: number;
  combo: number;
  comboMax: number;
  distance: number;
  missionComplete: boolean;
  styleChains: number;
}

export class ScoringSystem {
  private score = 0;
  private fares = 0;
  private bonuses = 0;
  private nearMisses = 0;
  private combo = 0;
  private comboMax = 0;
  private distance = 0;
  private missionComplete = false;
  private distanceAcc = 0;
  private styleChains = 0;
  private heatMultiplier = 1;

  reset(): void {
    this.score = 0;
    this.fares = 0;
    this.bonuses = 0;
    this.nearMisses = 0;
    this.combo = 0;
    this.comboMax = 0;
    this.distance = 0;
    this.missionComplete = false;
    this.distanceAcc = 0;
    this.styleChains = 0;
    this.heatMultiplier = 1;
  }

  setHeatMultiplier(mult: number): void {
    this.heatMultiplier = Math.max(1, mult);
  }

  get multiplier(): number {
    return (
      (1 + Math.min(this.combo, SCORING.comboMax) * SCORING.comboStep) * this.heatMultiplier
    );
  }

  addFare(): number {
    this.fares += 1;
    const gained = Math.round(SCORING.farePoints * this.multiplier);
    this.score += gained;
    this.combo = Math.min(this.combo + 1, SCORING.comboMax);
    this.comboMax = Math.max(this.comboMax, this.combo);
    if (!this.missionComplete && this.fares >= SCORING.missionFareTarget) {
      this.missionComplete = true;
      this.score += Math.round(SCORING.missionBonus * this.heatMultiplier);
    }
    return gained;
  }

  addBonus(): number {
    this.bonuses += 1;
    const gained = Math.round(SCORING.farePoints * 3 * this.multiplier);
    this.score += gained;
    this.combo = Math.min(this.combo + 1, SCORING.comboMax);
    this.comboMax = Math.max(this.comboMax, this.combo);
    return gained;
  }

  breakCombo(): void {
    this.combo = 0;
  }

  addNearMiss(rush = false): number {
    this.nearMisses += 1;
    this.combo = Math.min(this.combo + 1, SCORING.comboMax);
    this.comboMax = Math.max(this.comboMax, this.combo);
    let gained = Math.round(
      (SCORING.nearMissPoints + (rush ? SCORING.rushNearMissBonus : 0)) * this.heatMultiplier,
    );
    this.score += gained;
    return gained;
  }

  /** Returns style bonus points if a chain completes, else 0. */
  registerStyleHit(streak: number): number {
    if (streak > 0 && streak % SCORING.styleStreakNeeded === 0) {
      this.styleChains += 1;
      const gained = Math.round(SCORING.styleStreakBonus * this.heatMultiplier);
      this.score += gained;
      return gained;
    }
    return 0;
  }

  addDistance(pixels: number): void {
    this.distanceAcc += pixels;
    while (this.distanceAcc >= 100) {
      this.distanceAcc -= 100;
      this.distance += 100;
      this.score += Math.round(SCORING.distancePointsPer100 * this.heatMultiplier);
    }
  }

  snapshot(): ScoreSnapshot {
    return {
      score: this.score,
      fares: this.fares,
      bonuses: this.bonuses,
      nearMisses: this.nearMisses,
      combo: this.combo,
      comboMax: this.comboMax,
      distance: this.distance,
      missionComplete: this.missionComplete,
      styleChains: this.styleChains,
    };
  }
}
